/**
 * Helper to extract collectionContext from query parameters. This finds the collectionContext in the various ways it can be in the URL:
 * - as the cc param ex. https://www.familysearch.org/ark:/61903/3:1:S3HT-68PT-RH?wc=Q678-76V%3A179532601%26cc%3D1908531&cc=1908531&lang=en&i=0
 * - embedded in the wc param ex.https://www.familysearch.org/ark:/61903/3:1:S3HY-61MP-DP?wc=Q678-766%3A179532901%26cc%3D1908531&lang=en&i=4
 * @returns {string} collectionId
 */
export function getCollectionContextFromUrl() {
  const searchParams = new URLSearchParams(window.location.search)
  let context = searchParams.get('cc')
  // If not found, try to extract cc from wc param--sometimes it is embedded in there
  if (!context) {
    const wcParam = searchParams.get('wc')
    if (wcParam) {
      // decode wc param and parse for cc
      const decodedWc = decodeURIComponent(wcParam)
      const wcParams = new URLSearchParams(decodedWc.replace(/^[^?]*[?&]/, ''))
      context = wcParams.get('cc')
    }
  }
  return context
}

/**
 * Get the collection id from the waypoint parameter.
 * @param {string} owcParameter
 * @param {string} waypointURL
 * @returns {string} collectionId
 */
export default function getCollectionId(owcParameter, waypointURL = null) {
  if (owcParameter?.startsWith('http')) {
    // e.g. https://www.familysearch.org/service/cds/recapi/collections/1784216/waypoints -> 1784216
    const collectionIdMatchArray = owcParameter.match(/(?<=\/)[\d]+(?=\/waypoints)/)
    const collectionId = collectionIdMatchArray?.[0]

    return collectionId
  }

  if (waypointURL) {
    return (waypointURL.match(/cc=([^&]*)/) || [])?.[1]
  }
  return owcParameter
}

/**
 * Get the access information contained in the image rights for GedcomX image.
 * @param {Object} imageData The image data object
 * @returns {Object} A rights object with all of properties listed:
 *    restricted {Bool}
 *    allowDownload {Bool}
 *    allowPrint {Bool}
 *    authorized {Bool}
 */
export function getImageRights(imageData) {
  const access = {}
  const rightsString = imageData?.meta?.sourceDescriptions?.[0]?.rights?.[0]
  let rights = null

  if (rightsString) {
    rights = new URLSearchParams(rightsString.split('?')[1])
    // Convert 'true' or 'false' to boolean
    rights.forEach((value, key) => {
      if (value === 'true') {
        access[key] = true
      } else if (value === 'false') {
        access[key] = false
      } else {
        access[key] = decodeURIComponent(value)
      }
    })
    return access
  }

  return access
}

/**
 * Small helper function to clip index to max number of images
 * @param {Number} index - current index to be normalized to the length of the images array
 * @param {Number} maxLength -length of the images array (-1 is handled inside the function to make function calls cleaner)
 * @returns {Number}
 */
export function normalizeIndex(index, maxLength) {
  return maxLength ? Math.min(index, maxLength - 1) : index
}

/**
 * Extracts the title and url from imageData.
 * @param {Object} state - The state object.
 * @returns {Object} title and, if available, url
 */
export function getArtifactTitleAndUrl(imageData) {
  const title = imageData?.meta?.sourceDescriptions?.[0]?.titles?.[0]?.value?.split('; ')?.[0] ?? null
  const url = imageData?.meta?.sourceDescriptions?.[0]?.about
  if (!url) return { title, url: null }
  return { title, url }
}
